import { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react'

const translations = {
  en: {
    header: {
      title: 'Data Collection SDK',
      subtitle: 'Auto-Tracking Demo',
    },
    nav: {
      home: 'Home',
      docs: 'Documents',
      products: 'Products',
      cart: 'Cart',
      language: 'Language',
      zh: '中文',
      en: 'EN',
    },
    status: {
      title: 'SDK Status Monitor',
      current: 'Current Config:',
      recent: 'Recent Route Changes:',
      routeChanged: '{time} - Route changed: {path}',
    },
    home: {
      title: 'Welcome to the Data Collection SDK Demo',
      desc: 'This page shows how the SDK auto-tracks SPA route changes. When you click different nav links, the SDK will detect URL changes and trigger pageview events automatically.',
      howTo: '🧪 How to Test',
      howToDesc: 'Open your browser DevTools Console and click different nav links above. You will see a pageview event fire automatically on each route change.',
    },
    products: {
      title: 'Products',
      desc: 'Browse featured products. Clicking "Add to Cart" triggers the add_to_cart event.',
      add: 'Add to Cart',
      added: '{name} added to cart!',
      categories: {
        smartphone: 'Smartphone',
        laptop: 'Laptop',
        headphones: 'Headphones',
        smartwatch: 'Smartwatch',
        tablet: 'Tablet',
        keyboard: 'Keyboard',
      },
    },
    cart: {
      title: 'Cart',
      desc: 'Review your cart. Proceeding to checkout will trigger the checkout event.',
      items: '🛒 Cart Items',
      total: 'Total: {price}',
      checkout: '💳 Checkout Now',
      alert: 'Checkout complete! checkout event has fired.',
      promos: '🎁 Promotions',
      promoList: [
        'Free shipping over NT$ 30,000',
        '10% off on your first purchase',
        'Members get extra 5% off during birthday month',
        'Apple products include an original case',
      ],
      shipping: '🚚 Shipping Methods',
      shippingOptions: {
        home: 'Home Delivery: 1-2 business days, NT$ 100',
        store: 'Convenience Store Pickup: 2-3 business days, NT$ 60',
        pickup: 'In-Store Pickup: Same day pickup, free',
      },
    },
    docs: {
      title: 'Documentation',
      how: '🔧 How to Use',
      install: 'Installation',
      copy: 'Copy',
      addEvent: 'Add to Cart Event',
      params: 'Parameters',
      tableHeaders: {
        field: 'Field',
        type: 'Type',
        required: 'Required',
        desc: 'Description',
        example: 'Example',
      },
      required: 'Yes',
      optional: 'No',
      cartDesc: 'Cart items',
      cartHint: '(event will not send without this)',
      itemId: 'Unique product ID',
      itemName: 'Product name (defaults to N/A if missing)',
      quantity: 'Quantity',
      price: 'Unit price',
      totalPrice: 'Item subtotal (defaults to price × quantity)',
      exampleLabel: 'Example',
      checkoutEvent: 'Checkout Event',
      checkoutParams: 'Checkout (checkout) Parameters',
      orderId: 'Unique order ID',
      orderTotal: 'Order total (including shipping). If omitted, calculated from items plus shipping.',
      shippingCost: 'Shipping cost',
      setupHint: '<!-- Include in HTML; the SDK auto-initializes and starts tracking -->',
    },
  },
  zh: {
    header: {
      title: 'Data Collection SDK',
      subtitle: '自動追蹤示範',
    },
    nav: {
      home: '首頁',
      docs: '文件',
      products: '商品列表',
      cart: '購物車',
      language: '語言',
      zh: '中文',
      en: 'EN',
    },
    status: {
      title: 'SDK 狀態監控',
      current: '當前設定:',
      recent: '最近的路由變化:',
      routeChanged: '{time} - 路由變更: {path}',
    },
    home: {
      title: '歡迎使用 Data Collection SDK Demo',
      desc: '這是展示 SDK 自動追蹤 SPA 路由變化的頁面。點擊不同導航連結時，SDK 會偵測 URL 變化並自動觸發 pageview 事件。',
      howTo: '🧪 測試說明',
      howToDesc: '請開啟瀏覽器 DevTools 的 Console，點擊上方不同的導航連結，每次路由切換都會自動觸發 pageview 事件。',
    },
    products: {
      title: '商品列表',
      desc: '瀏覽精選商品，點擊「加入購物車」會觸發 add_to_cart 事件。',
      add: '加入購物車',
      added: '已將 {name} 加入購物車！',
      categories: {
        smartphone: '智慧手機',
        laptop: '筆記型電腦',
        headphones: '耳機',
        smartwatch: '智慧手錶',
        tablet: '平板電腦',
        keyboard: '鍵盤',
      },
    },
    cart: {
      title: '購物車',
      desc: '查看購物車內容，進行結帳會觸發 checkout 事件。',
      items: '🛒 購物車商品',
      total: '總計: {price}',
      checkout: '💳 立即結帳',
      alert: '結帳完成！checkout 事件已觸發。',
      promos: '🎁 優惠活動',
      promoList: [
        '滿 NT$ 30,000 免運費',
        '首次購買享 9 折優惠',
        '會員生日當月享額外 5% 折扣',
        '購買蘋果產品送原廠保護殼',
      ],
      shipping: '🚚 配送方式',
      shippingOptions: {
        home: '宅配: 1-2 個工作天，運費 NT$ 100',
        store: '超商取貨: 2-3 個工作天，運費 NT$ 60',
        pickup: '門市自取: 當日可取，免運費',
      },
    },
    docs: {
      title: '文件',
      how: '🔧 使用方式',
      install: '安裝設置',
      copy: '複製',
      addEvent: '加入購物車事件',
      params: '參數',
      tableHeaders: {
        field: '欄位名稱',
        type: '類型',
        required: '必填',
        desc: '說明',
        example: '範例',
      },
      required: '是',
      optional: '否',
      cartDesc: '購物車商品清單',
      cartHint: '(若沒傳事件不會送出)',
      itemId: '商品唯一識別碼',
      itemName: '商品名稱 (若沒傳會以 N/A 顯示)',
      quantity: '購買數量',
      price: '單價',
      totalPrice: '該商品小計（若沒傳，則以 price × quantity 計算）',
      exampleLabel: '範例',
      checkoutEvent: '結帳事件',
      checkoutParams: '結帳事件 (checkout) 參數',
      orderId: '訂單唯一識別碼',
      orderTotal: '訂單總金額（含運費，若沒傳則以 items 加總再加運費）',
      shippingCost: '運費金額',
      setupHint: '<!-- 在 HTML 中載入，SDK 會自動初始化並開始追蹤 -->',
    },
  },
}

const LocaleContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (key, vars) => key && vars,
})

const interpolate = (template, vars = {}) =>
  template.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`))

const getValue = (obj, key) => key.split('.').reduce((acc, part) => acc?.[part], obj)

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === 'undefined') return 'en'
    return localStorage.getItem('locale') || (navigator.language.startsWith('zh') ? 'zh' : 'en')
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('locale', locale)
    }
  }, [locale])

  const setLocale = useCallback((next) => setLocaleState(next), [])

  const t = useCallback((key, vars) => {
    const template = getValue(translations[locale], key) ?? key
    if (typeof template === 'string') return interpolate(template, vars)
    if (Array.isArray(template)) return template
    return template ?? key
  }, [locale])

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export const useLocale = () => useContext(LocaleContext)
